import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  CreditCardIcon, 
  SparklesIcon, 
  ChartBarIcon,
  ScaleIcon
} from '@heroicons/react/24/outline'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts'
import AuthHeader from '../components/AuthHeader'
import HotelRefCard from '../components/HotelRefCard'
import FlightRefCard from '../components/FlightRefCard'
import { buildApiUrl } from '../config/api'

function Landing() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [recentRedemptions, setRecentRedemptions] = useState([])
  const [loyaltyPrograms, setLoyaltyPrograms] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedProgram, setSelectedProgram] = useState('All Programs')
  const [cppTrends, setCppTrends] = useState([])
  const [cppLoading, setCppLoading] = useState(false)
  const [popularLoyaltyPrograms, setPopularLoyaltyPrograms] = useState({ mostPopular: [], trending: [] })
  const [loyaltyProgramsLoading, setLoyaltyProgramsLoading] = useState(false)

  // Add logging when component mounts
  useEffect(() => {
    // console.log('🏠 Landing page component mounted')
    // console.log('📍 Current URL:', window.location.href)
    // console.log('👤 User context:', user)
    // console.log('🔍 Starting to fetch data...')
  }, [user])

  // Sample data for guest users
  const sampleRedemptions = [
    {
      id: 1,
      program: 'American Airlines AAdvantage',
      type: 'Flight',
      points: 25000,
      cashValue: 500,
      cpp: 0.020,
      date: '2024-01-15',
      route: 'LAX → JFK',
      class: 'Economy'
    },
    {
      id: 2,
      program: 'Marriott Bonvoy',
      type: 'Hotel',
      points: 35000,
      cashValue: 350,
      cpp: 0.010,
      date: '2024-01-10',
      location: 'New York',
      nights: 2
    },
    {
      id: 3,
      program: 'Delta SkyMiles',
      type: 'Flight',
      points: 40000,
      cashValue: 800,
      cpp: 0.020,
      date: '2024-01-05',
      route: 'SFO → LHR',
      class: 'Premium Economy'
    },
    {
      id: 4,
      program: 'Hilton Honors',
      type: 'Hotel',
      points: 45000,
      cashValue: 450,
      cpp: 0.010,
      date: '2024-01-01',
      location: 'London',
      nights: 3
    }
  ]

  // Sample loyalty programs for the discover section
  const samplePrograms = [
    { name: 'American Airlines AAdvantage', loyalty_currency: 'AAdvantage Miles' },
    { name: 'Delta SkyMiles', loyalty_currency: 'SkyMiles' },
    { name: 'United MileagePlus', loyalty_currency: 'MileagePlus Miles' },
    { name: 'Marriott Bonvoy', loyalty_currency: 'Bonvoy Points' },
    { name: 'Hilton Honors', loyalty_currency: 'Honors Points' },
    { name: 'Hyatt World of Hyatt', loyalty_currency: 'World of Hyatt Points' }
  ]

  // Fetch popular loyalty programs from backend
  const fetchPopularLoyaltyPrograms = async () => {
    setLoyaltyProgramsLoading(true)
    try {
      const apiUrl = buildApiUrl('/api/redemptions/popular-loyalty-programs')
      // console.log('🔍 Fetching popular loyalty programs from:', apiUrl)
      // console.log('🔍 Full URL being called:', apiUrl)
      const response = await fetch(apiUrl)
      // console.log('📡 Response status:', response.status)
      // console.log('📡 Response headers:', response.headers)
      
      if (response.ok) {
        const data = await response.json()
        // console.log('🔍 Popular Loyalty Programs API Response:', data)
        if (data.mostPopular && data.trending) {
          setPopularLoyaltyPrograms(data)
        } else {
          // console.warn('⚠️ API response missing expected data structure:', data)
          // Fallback to sample data if API response is malformed
          setPopularLoyaltyPrograms({
            mostPopular: samplePrograms.slice(0, 3),
            trending: samplePrograms.slice(3, 6)
          })
        }
      } else {
        const errorText = await response.text()
        // console.error('❌ Failed to fetch popular loyalty programs:', response.status, errorText)
        // Fallback to sample data if API fails
        setPopularLoyaltyPrograms({
          mostPopular: samplePrograms.slice(0, 3),
          trending: samplePrograms.slice(3, 6)
        })
      }
    } catch (error) {
      // console.error('❌ Error fetching popular loyalty programs:', error)
      // Fallback to sample data if API fails
      setPopularLoyaltyPrograms({
        mostPopular: samplePrograms.slice(0, 3),
        trending: samplePrograms.slice(3, 6)
      })
    } finally {
      setLoyaltyProgramsLoading(false)
    }
  }

  // Fetch recent redemptions from backend
  const fetchRecentRedemptions = async () => {
    setLoading(true)
    try {
      const apiUrl = buildApiUrl('/api/redemptions/recent?limit=4&orderBy=created_at&sort=desc')
      // console.log('🔍 Fetching recent redemptions from:', apiUrl)
      // console.log('🔍 Full URL being called:', apiUrl)
      const response = await fetch(apiUrl)
      if (response.ok) {
        const data = await response.json()
        // console.log('🔍 Backend API Response - Recent Redemptions:')
        // console.log('Raw data:', data)
        
        if (data.redemptions && data.redemptions.length > 0) {
          // console.log('📊 Redemptions ordered by travel_start_date (should be descending):')
          // data.redemptions.forEach((redemption, index) => {
          //   console.log('${index + 1}. ID: ${redemption.id}')
          //   console.log('   Type: ${redemption.redemption_type}')
          //   console.log('   Program: ${redemption.program?.name}')
          //   console.log('   Travel Start Date: ${redemption.travel_start_date}')
          //   console.log('   Points: ${redemption.points_spent}')
          //   console.log('   CPP: ${redemption.value_cpp}')
          //   console.log('   ---')
          // })
        } else {
          // console.log('⚠️ No redemptions returned from backend')
        }
        
        // console.log('✅ Setting recentRedemptions state with backend data:', data.redemptions?.length || 0, 'redemptions')
        setRecentRedemptions(data.redemptions || [])
      } else {
        // console.error('❌ Failed to fetch recent redemptions - HTTP status:', response.status)
        // console.log('🔄 Falling back to sample data')
        setRecentRedemptions(sampleRedemptions)
      }
    } catch (error) {
      // console.error('❌ Error fetching recent redemptions:', error)
      // console.log('🔄 Falling back to sample data due to error')
      setRecentRedemptions(sampleRedemptions)
    } finally {
      setLoading(false)
    }
  }

  // Fetch loyalty programs from backend
  const fetchLoyaltyPrograms = async () => {
    try {
      const apiUrl = buildApiUrl('/api/loyalty-programs')
      // console.log('🔍 Fetching loyalty programs from:', apiUrl)
      // console.log('🔍 Full URL being called:', apiUrl)
      const response = await fetch(apiUrl)
      if (response.ok) {
        const data = await response.json()
        // console.log('Loyalty programs API response:', data);
        // console.log('Programs array:', data.programs);
        if (data.programs && data.programs.length > 0) {
          // console.log('First program sample:', data.programs[0]);
          // console.log('First program loyalty_currency:', data.programs[0]?.loyalty_currency);
        }
        setLoyaltyPrograms(data.programs || samplePrograms)
      } else {
        // console.error('Failed to fetch loyalty programs')
        // console.log('Falling back to sample programs:', samplePrograms)
        setLoyaltyPrograms(samplePrograms)
      }
    } catch (error) {
      // console.error('Error fetching loyalty programs:', error)
      setLoyaltyPrograms(samplePrograms)
    }
  }

  // Fetch CPP trends from backend
  const fetchCPPTrends = async (program = null) => {
    setCppLoading(true)
    try {
      let url = buildApiUrl('/api/redemptions/cpp-trends?months=6')
      if (program && program !== 'All Programs') {
        url += '&program=' + encodeURIComponent(program)
        // console.log('🔍 Fetching CPP trends for program:', program)
        // console.log('🔍 URL being called:', url)
      } else {
        // console.log('🔍 Fetching CPP trends for all programs')
        // console.log('🔍 Full URL being called:', url)
      }
      
      const response = await fetch(url)
      if (response.ok) {
        const data = await response.json()
        // console.log('📊 CPP Trends from backend:', data)
        setCppTrends(data.trends || [])
      } else {
        // console.error('Failed to fetch CPP trends')
        setCppTrends([])
      }
    } catch (error) {
      // console.error('Error fetching CPP trends:', error)
      setCppTrends([])
    } finally {
      setCppLoading(false)
    }
  }

  useEffect(() => {
    // Fetch real data from the backend APIs
    fetchRecentRedemptions()
    fetchLoyaltyPrograms()
    fetchCPPTrends()
    fetchPopularLoyaltyPrograms()
  }, [])

  // Refetch CPP trends when selectedProgram changes
  useEffect(() => {
    if (selectedProgram) {
      fetchCPPTrends(selectedProgram)
    }
  }, [selectedProgram])

  // Add error boundary for the component
  try {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <AuthHeader />

      {/* Hero Section */}
      <section className="py-20" style={{ background: 'linear-gradient(to bottom, #D9E4F8, #ffffff)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl md:text-3xl text-gray-900 mb-12 uppercase" style={{ lineHeight: '1.6', fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.05em', maxWidth: '900px', margin: '0 auto' }}>
            SEARCH REAL-WORLD REDEMPTIONS AND SEE IF YOU'RE GETTING A GREAT DEAL FOR YOUR POINTS
          </h1>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 mt-4">
            <Link
              to="/compare-redemption"
              className="inline-flex items-center justify-center px-8 py-4 text-white rounded-lg transition-colors shadow-lg hover:shadow-xl"
              style={{ backgroundColor: '#548BCB' }}
            >
              <ScaleIcon className="w-6 h-6 mr-2" />
              Compare My Redemption
            </Link>
            
            <Link
              to="/check-redemption-value"
              className="inline-flex items-center justify-center px-8 py-4 text-white rounded-lg transition-colors shadow-lg hover:shadow-xl"
              style={{ backgroundColor: '#7395C2' }}
            >
              <CreditCardIcon className="w-6 h-6 mr-2" />
              Check Redemption Value
            </Link>
          </div>
          

        </div>
      </section>

      {/* Recent Redemptions Section */}
      <section className="py-16" style={{ backgroundColor: '#F7FAFF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl text-gray-800 text-center mb-12" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.03em', lineHeight: '1.3' }}>
            Recent Redemptions
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {loading ? (
              // Loading state
              Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-3"></div>
                  <div className="h-3 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded"></div>
                </div>
              ))
            ) : (
              // Actual redemptions
              Array.isArray(recentRedemptions) && recentRedemptions.length > 0 ? (
                recentRedemptions.map((redemption) => (
                  redemption.hotel ? (
                    // For hotels, show only the HotelRefCard
                    <HotelRefCard
                      key={redemption.id}
                      hotel={redemption.hotel.hotel}
                      checkInDate={redemption.hotel.check_in_date}
                      checkOutDate={redemption.hotel.check_out_date}
                      price={redemption.cash_price}
                      points={redemption.points_spent}
                      cpp={redemption.value_cpp}
                      addedDate={redemption.created_at}
                    />
                  ) : (
                    // For flights, show the FlightRefCard
                    <FlightRefCard
                      key={redemption.id}
                      flight={redemption.flight}
                      price={redemption.cash_price}
                      points={redemption.points_spent}
                      cpp={redemption.value_cpp}
                      addedDate={redemption.created_at}
                      loyaltyCurrency={redemption.program?.loyalty_currency}
                    />
                  )
                ))
              ) : (
                // Fallback content when no redemptions
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-500">No recent redemptions found</p>
                </div>
              )
            )}
          </div>
          
        </div>
      </section>

      {/* Make Every Point Count Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl text-gray-800 text-center mb-12" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.03em', lineHeight: '1.3' }}>
            MAKE EVERY POINT COUNT
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Explore Benchmarks Card */}
            <div className="rounded-lg p-8 text-center hover:shadow-lg transition-all duration-300 shadow-md" style={{ backgroundColor: '#F7FAFF' }}>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-lg text-gray-800 mb-4" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.02em', lineHeight: '1.3' }}>
                EXPLORE BENCHMARKS
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em', lineHeight: '1.6' }}>
                Search any route, cabin, or program to see the average cents-per-point and what most travelers are paying.
              </p>
            </div>

            {/* Compare Your Redemption Card */}
            <div className="rounded-lg p-8 text-center hover:shadow-lg transition-all duration-300 shadow-md" style={{ backgroundColor: '#F7FAFF' }}>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <h3 className="text-lg text-gray-800 mb-4" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.02em', lineHeight: '1.3' }}>
                COMPARE YOUR REDEMPTION
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em', lineHeight: '1.6' }}>
                Enter your own redemption details to instantly see if you beat the average or scored a top-tier deal.
              </p>
            </div>

            {/* Know When You're Winning Card */}
            <div className="rounded-lg p-8 text-center hover:shadow-lg transition-all duration-300 shadow-md md:col-span-2 lg:col-span-1" style={{ backgroundColor: '#F7FAFF' }}>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-lg text-gray-800 mb-4" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.02em', lineHeight: '1.3' }}>
                KNOW WHEN YOU'RE WINNING
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed" style={{ fontFamily: 'Maven Pro, sans-serif', fontWeight: '400', letterSpacing: '0.01em', lineHeight: '1.6' }}>
                See percentile rankings, best-value programs, and trending sweet spots based on real traveler data.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Discover By Section - Hidden for now */}
      {/* <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 lg:mr-8">
              <h2 className="text-2xl font-bold text-gray-900">Discover By</h2>
              <button
                onClick={() => setSelectedProgram('All Programs')}
                className={'px-6 py-3 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md ' + (
                    selectedProgram === 'All Programs' 
                      ? 'bg-primary-600 text-white shadow-lg transform scale-105' 
                      : 'bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 text-gray-700 border border-gray-200'
                  )}
              >
                All Programs
              </button>
            </div>
          </div>
        </div>
      </section> */}

      {/* CPP Over Time by Program Chart - Hidden for now */}
      {/* <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedProgram === 'All Programs' ? 'CPP Trends - All Loyalty Programs' : `CPP Trends - ${selectedProgram}`}
            </h3>
            {cppLoading ? (
              <div className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading CPP trends...</p>
                </div>
              </div>
            ) : cppTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={cppTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value.toFixed(2)}¢`, 'CPP']} />
                  {cppTrends.length > 0 && cppTrends[0] && Object.keys(cppTrends[0])
                    .filter(key => key !== 'month')
                    .map((programName, index) => {
                      const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#6B7280', '#059669', '#DC2626', '#1E40AF', '#8B5CF6'];
                      const color = colors[index % colors.length];
                      
                      return (selectedProgram === 'All Programs' || selectedProgram === programName) ? (
                        <Line 
                          key={programName}
                          type="monotone" 
                          dataKey={programName} 
                          stroke={color} 
                          strokeWidth={2} 
                        />
                      ) : null;
                    })}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-gray-500 mb-4">No CPP data available</p>
                  <p className="text-sm text-gray-400">Add redemptions to see CPP trends over time</p>
                </div>
              </div>
            )}
            {!user && cppTrends.length > 0 && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Real redemption data shown • Sign up to track your own CPP trends
              </p>
            )}
          </div>
        </div>
      </section> */}
    </div>
    )
  } catch (error) {
    // console.error('Error rendering Landing component:', error)
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
          <p className="text-gray-600 mb-4">We're having trouble loading the landing page.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Reload Page
          </button>
        </div>
      </div>
    )
  }
}

export default Landing
